import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import { atom, useRecoilState } from 'recoil'

import { IDLE_TIMEOUT } from '@/modules/core/constants/idle-timer.constants'
import { isProduction } from '@/utils/environment'

export const lockoutAtom = atom<boolean | null>({
  key: 'lockout',
  default: null,
  effects: [
    ({ setSelf, trigger }) => {
      if (trigger !== 'get') return

      // Native: always locked on cold start (biometrics or eHealth required)
      if (Capacitor.isNativePlatform()) {
        setSelf(true)
        return
      }

      // Web: check if idle timeout expired while the browser was closed
      const checkIdleState = async () => {
        try {
          // In non-production, respect debug settings for idle timeout
          let idleTimeout = IDLE_TIMEOUT
          if (!isProduction()) {
            const debugResult = await Preferences.get({
              key: '@app/debug-settings',
            })
            if (debugResult?.value) {
              const debugSettings = JSON.parse(debugResult.value)
              if (debugSettings?.idleTimeout) {
                idleTimeout = debugSettings.idleTimeout
              }
            }
          }

          const result = await Preferences.get({ key: '@app/local-timer' })
          if (result?.value) {
            const lastInteraction = JSON.parse(result.value)
            if (typeof lastInteraction === 'number' && lastInteraction + idleTimeout < Date.now()) {
              setSelf(true)
              return
            }
          }
          setSelf(false)
        } catch {
          setSelf(false)
        }
      }
      checkIdleState()
    },
  ],
})

/**
 * Hook to get and set the lockout state.
 *
 * Lockout initializes from the idle timer state:
 * - Native: always true on cold start
 * - Web: true if idle timeout expired, false otherwise
 * - null while loading (web async check)
 */
function useLockoutTime() {
  const [lockout, setLockoutState] = useRecoilState(lockoutAtom)

  // On native, treat null (atom default before effect resolves) as locked.
  // Recoil's setSelf in atom effects causes a two-render cycle: first with
  // the default (null), then with the set value (true). This derivation
  // ensures consumers see true immediately on native without waiting for
  // the second render.
  // On web, null stays null so consumers can show a loader while the async
  // idle-timer check completes.
  const resolvedLockout = lockout === null && Capacitor.isNativePlatform() ? true : lockout

  const setLockout = (value: boolean | null) => {
    setLockoutState(value)
  }

  return {
    lockout: resolvedLockout,
    setLockout,
  }
}

export { useLockoutTime }
